import React, { useState } from "react";
import { Form, Input, InputNumber, Spin, Row, Col, Popconfirm, Button } from "antd";
import { isEmpty } from "lodash";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import * as turf from "@turf/turf";

import "./Location.scss";
import AddressInput1 from "../../Inputs/AddressInput1";
import { SubmitIcon, DeleteIcon } from "components/Icons";
import { PrimaryButton, TextButton } from 'components/Buttons';

import { doUpdateLocation, doCreateLocation, doDeleteLocation } from "modules/location/stores/thunk";
import { doDetailZone } from "modules/zone/stores/thunk";

const LocationForm = ({
  zone,
  circle,
  form,
  state,
  isFetching,
  handleCancel,
  onClosePopup,
}) => {
  const action = state.action;
  const dispatch = useDispatch();
  const [clickedLabels, setSlickedLabels] = useState([]);
  const customizeLabel = (label) => (
    <>
      {label}
      {clickedLabels.includes(label) ? (
        <CaretUpOutlined onClick={() => handleClickLabel(label)} />
      ) : (
        <CaretDownOutlined onClick={() => handleClickLabel(label)} />
      )}
    </>
  );
  const customizeClassName = (label, name) => (
    clickedLabels.includes(label) || form.getFieldValue(name) ? 'open' : 'close'
  );
  const handleClickLabel = (label) => {
    const clickedLabelsT = [...clickedLabels];
    if (clickedLabels.includes(label)) {
      setSlickedLabels(clickedLabelsT.filter((l) => l !== label));
    } else {
      clickedLabelsT.push(label);
      setSlickedLabels(clickedLabelsT);
    }
  };

  const handleDelete = async () => {
    await dispatch(doDeleteLocation(state.location._id));
    handleCancel();
    onClosePopup();
    await dispatch(doDetailZone(zone._id));
  };

  const handleSubmit = async (values) => {
    const params = { ...values };
    if (values.location) {
      params.address = values.location.address;
      params.lat = values.location.latLng.lat;
      params.lng = values.location.latLng.lng;
    } else {
      params.address = values.currentAddress;
    }
    if (state.action === 'new') {
      await dispatch(doCreateLocation({ ...params, zone: zone._id }));
    } else {
      const point = turf.point([params.lng, params.lat]);
      const polygon = turf.polygon([...circle.geometry.coordinates]);
      params.updatedInZone = turf.booleanPointInPolygon(point, polygon);
      await dispatch(doUpdateLocation({ id: state.location._id, ...params }));
    }
    handleCancel();
    onClosePopup();
    if (state.action === 'new') {
      await dispatch(doDetailZone(zone._id));
    }
  };

  return (
    <Spin spinning={isFetching}>
      <div className="LocationForm">
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleSubmit}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <div className="FormHeader">
            <div className="left">
              {action === 'new' ? "New Address" : action === 'enter_latlng' ? "Redigera Lat/long" : "Redigera adress"}
            </div>
          </div>
          <div className="FormContent">
            {action !== 'new' && (
              <Form.Item
                label="Nuvarande Adress"
                name="currentAddress"
                style={{ width: "100%" }}
              >
                <Input
                  size="large"
                  disabled
                />
              </Form.Item>
            )}
            {action === 'new' || action === 'enter_address' ? (
              <Form.Item
                label="Uppdaterad adress"
                name="location"
                rules={[
                  {
                    validator: (_, value) => {
                      if (
                        value?.address &&
                        value?.latLng?.lat &&
                        value?.latLng?.lng
                      ) {
                        return Promise.resolve();
                      }

                      if (isEmpty(value?.address)) {
                        return Promise.reject(new Error("Required"));
                      }

                      return Promise.reject(new Error("Invalid"));
                    },
                  },
                ]}
              >
                <AddressInput1
                  size="large"
                  placeholder="Uppdaterad adress"
                  onSelect={(value) =>
                    form.setFieldsValue({ location: value })
                  }
                />
              </Form.Item>
            ) : (
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    label="Lat"
                    name="lat"
                    rules={[
                      { required: true, message: "Required" }, 
                      {
                        pattern: /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/,
                        message: 'Invalid',
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Lat"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Long"
                    name="lng"
                    rules={[
                      { required: true, message: "Required" }, 
                      {
                        pattern: /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/,
                        message: 'Invalid',
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Long"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {/*<Form.Item
              label="Note"
              name="note"
            >
              <Input
                size="large"
                placeholder="Note"
              />
            </Form.Item>*/}
            <Form.Item
              className={customizeClassName('Fastighetsbeteckning', 'propertyDesignation')}
              label={customizeLabel('Fastighetsbeteckning')}
              name="propertyDesignation"
            >
              <Input
                size="large"
                placeholder="Fastighetsbeteckning"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Fastighetsnotering', 'realEstateListing')}
              label={customizeLabel('Fastighetsnotering')}
              name="realEstateListing"
            >
              <Input
                size="large"
                placeholder="Fastighetsnotering"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Arbetsorderflaggning', 'workOrderFlag')}
              label={customizeLabel('Arbetsorderflaggning')}
              name="workOrderFlag"
            >
              <Input
                size="large"
                placeholder="Arbetsorderflaggning"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Registrerings nummer', 'registrationNumber')}
              label={customizeLabel('Registrerings nummer')}
              name="registrationNumber"
            >
              <Input
                size="large"
                placeholder="Registrerings nummer"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Övriga noteringar 1', 'freeTextField1')}
              label={customizeLabel('Övriga noteringar 1')}
              name="freeTextField1"
            >
              <Input
                size="large"
                placeholder="Övriga noteringar 1"
              />
            </Form.Item>
            <Form.Item
              className={customizeClassName('Övriga noteringar 2', 'freeTextField2')}
              label={customizeLabel('Övriga noteringar 2')}
              name="freeTextField2"
            >
              <Input
                size="large"
                placeholder="Övriga noteringar 2"
              />
            </Form.Item>
          </div>
          <div className="FormFooter">
            <div className="Left">
              {action !== 'new' && (
                <Popconfirm
                  title="Radera adress"
                  description="Är du säker på att du vill radera denna adress?"
                  onConfirm={handleDelete}
                  okText="Ja"
                  cancelText="Nej"
                >
                  <Button className="delete" danger><DeleteIcon /></Button>
                </Popconfirm>
              )}
            </div>
            <div className="Right">
              <TextButton
                key="cancel"
                label="Avbryt"
                onClick={handleCancel}
              />
              <PrimaryButton
                htmlType="submit"
                type="primary"
                label="Bekräfta"
                icon={<SubmitIcon />}
              />
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default LocationForm;
